import React from "react";
import { RefreshCw, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Pill, PillKind } from "../components/ui/pill";
import { Button } from "../components/ui/button";
import { StatCard } from "../components/ui/stat-card";
import { SectionLabel } from "../components/ui/section-label";
import { useSystemStatus } from "../hooks/queries";
import { IntegrationStatus } from "../api/api";

function formatTimestamp(iso: string | null): string {
  if (!iso) return "Nooit";
  return new Date(iso).toLocaleString("nl-NL", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function integrationPillKind(status: IntegrationStatus["status"]): PillKind {
  if (status === "ok") return "health-ok";
  if (status === "error") return "health-error";
  return "health-unconfigured";
}

function integrationLabel(status: IntegrationStatus["status"]): string {
  if (status === "ok") return "OK";
  if (status === "error") return "Fout";
  if (status === "disabled") return "Uitgeschakeld";
  return "Niet geconfigureerd";
}

const INTEGRATION_NAMES: Record<string, string> = {
  bullhorn: "Bullhorn",
  resend: "Resend",
  teams: "Microsoft Teams",
};

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4 rounded-card border border-waves-line bg-white p-6">
      <SectionLabel>{title}</SectionLabel>
      {children}
    </div>
  );
}

export default function Systeemstatus() {
  const { data, isLoading, isError, refetch, isFetching } = useSystemStatus({ silenceErrors: true });
  const navigate = useNavigate();

  return (
    <Layout
      title="Systeemstatus"
      subtitle="Technische status van backend, database en integraties"
      actions={
        <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={isFetching ? "animate-spin" : ""} />
          Vernieuwen
        </Button>
      }
    >
      <div className="mx-auto flex max-w-[1000px] flex-col gap-5">
        {isLoading ? (
          <div className="text-sm text-waves-muted">Laden…</div>
        ) : isError || !data ? (
          <div className="rounded-nav bg-status-alert-bg px-4 py-2.5 text-sm font-medium text-status-alert-fg">Kon systeemstatus niet ophalen.</div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              <Card title="Backend versie">
                <span className="break-all font-mono text-[15px] font-semibold text-waves-ink">{data.backend_version}</span>
              </Card>

              <Card title="Database migratie">
                {data.migration.available ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-[15px] font-semibold text-waves-ink">{data.migration.version}</span>
                    <Pill kind={data.migration.dirty ? "health-error" : "health-ok"}>
                      {data.migration.dirty ? "Vervuild" : "Schoon"}
                    </Pill>
                  </div>
                ) : (
                  <span className="text-sm text-waves-muted">Niet beschikbaar (dev-omgeving)</span>
                )}
              </Card>

              <Card title="Sheet synchronisatie">
                <dl className="flex flex-col gap-1.5 text-sm">
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-waves-body">Laatste push</dt>
                    <dd className="font-mono text-[13px] text-waves-ink">{formatTimestamp(data.sheet_sync.last_push)}</dd>
                  </div>
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-waves-body">Laatste pull</dt>
                    <dd className="font-mono text-[13px] text-waves-ink">{formatTimestamp(data.sheet_sync.last_pull)}</dd>
                  </div>
                </dl>
              </Card>
            </div>

            <Card title="Integraties">
              <div className="flex flex-col">
                {(Object.keys(data.integrations) as (keyof typeof data.integrations)[]).map((key, i) => {
                  const integration = data.integrations[key];
                  return (
                    <div key={key} className={`flex items-center justify-between gap-4 py-3 ${i > 0 ? "border-t border-waves-line" : ""}`}>
                      <span className="text-[15px] font-bold text-waves-ink">{INTEGRATION_NAMES[key] ?? key}</span>
                      <div className="flex min-w-0 items-center gap-3">
                        {integration.error && (
                          <span className="min-w-0 max-w-[320px] truncate text-[13px] text-waves-muted" title={integration.error}>{integration.error}</span>
                        )}
                        <Pill kind={integrationPillKind(integration.status)}>{integrationLabel(integration.status)}</Pill>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <h2 className="text-lg font-black tracking-[-0.01em] text-waves-ink">E-mails</h2>
                  <p className="text-sm text-waves-muted">Aantal e-mails per periode</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate("/systeemstatus/emails")}>
                  Bekijk e-mails <ArrowRight />
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                <StatCard title="Laatste 24 uur" value={<span className="font-mono">{data.email_counts.last_1_day.toLocaleString("nl-NL")}</span>} />
                <StatCard title="Laatste 7 dagen" value={<span className="font-mono">{data.email_counts.last_7_days.toLocaleString("nl-NL")}</span>} />
                <StatCard title="Laatste 30 dagen" value={<span className="font-mono">{data.email_counts.last_30_days.toLocaleString("nl-NL")}</span>} />
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
